import React from 'react';
import PropTypes from 'prop-types';
import useEntitlement from 'hooks/useEntitlement';
import UpgradePrompt from './UpgradePrompt';

/**
 * Renders children only when the store's plan includes a feature.
 *
 * This is the single gating pattern in the app — every gated surface uses it, so
 * there is one place where "locked" is decided and one place to change if the
 * treatment ever needs to differ.
 *
 * The backend masks gated payloads independently, so this component controls
 * presentation, not access. A user who bypasses it still gets nothing back.
 *
 * @param {String} feature feature code, e.g. 'ai_summary'
 * @param {ReactNode} children what to render when the plan includes it
 * @param {ReactNode} fallback what to render when it does not
 * @param {String} message upgrade copy, when relying on the default fallback
 * @param {Boolean} silent render nothing at all when locked
 * @param {String} variant passed through to the default upgrade prompt
 * @returns {ReactComponent} the gated content or its fallback
 */

const FeatureGate = ({
  feature,
  children,
  fallback,
  message,
  silent = false,
  variant = 'inline',
}) => {
  const { isEnabled, isResolved } = useEntitlement(feature);

  // Render nothing until entitlements are confirmed. Showing the children on an
  // unconfirmed guess would flash gated content to a store that has not paid
  // for it, which is the failure mode that matters here.
  if (!isResolved) {
    return null;
  }

  if (isEnabled) {
    return <>{children}</>;
  }

  if (silent) {
    return null;
  }

  if (fallback !== undefined) {
    return <>{fallback}</>;
  }

  return <UpgradePrompt message={message} variant={variant} />;
};

FeatureGate.propTypes = {
  feature: PropTypes.string.isRequired,
  children: PropTypes.node,
  fallback: PropTypes.node,
  message: PropTypes.string,
  silent: PropTypes.bool,
  variant: PropTypes.oneOf(['inline', 'block']),
};

export default FeatureGate;
